import { useCallback, useMemo, useState } from "react";
import { Badge, Flex, Section, Text } from "@radix-ui/themes";
import { Home03Icon } from "@/v2/assets/icons/Home03Icon";
import { Ticket03Icon } from "@/v2/assets/icons/Ticket03Icon";
import { Comment01Icon } from "@/v2/assets/icons/Comment01Icon";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { PageHeader } from "@/v2/components/layout";
import { DateRange } from "@/components/shared/date-range-picker/DateRangePicker";
import { ExploreInteractionType } from "@/v2/entities/explore/Explore.model";
import { useExploreCount } from "@/v2/entities/explore/Explore.api";
import { BillingHeaderActions } from "./BillingHeaderActions";
import {
  TrendMetricCard,
  TrendMetricTooltip,
} from "@/v2/features/home/TrendMetrics";
import { mergeFilter } from "@/v2/entities/explore/Explore.filter";
import { ResolutionStatusFormatter } from "@/v2/entities/explore/formatters/ResolutionStatusFormatter";

function TotalQuackHandledTooltip() {
  return (
    <TrendMetricTooltip
      title="Total Quack Handled Items"
      description={
        <>
          {`Combined total of all tickets and conversations that were successfully handled by Quack for the selected month, including `}
          <Badge variant="soft" color="green" highContrast>
            <ResolutionStatusFormatter value="RESOLVED" />
          </Badge>
          {" and "}
          <Badge variant="soft" color="blue" highContrast>
            <ResolutionStatusFormatter value="EXPECTED_ESCALATION" />
          </Badge>
          {" cases."}
        </>
      }
    />
  );
}

function TotalQuackHandledCard({
  ticketFilter,
  conversationFilter,
}: {
  ticketFilter: any;
  conversationFilter: any;
}) {
  const { data: ticketCount, isLoading: ticketLoading } = useExploreCount({
    source: "tickets",
    filter: ticketFilter,
  });

  const { data: conversationCount, isLoading: conversationLoading } =
    useExploreCount({
      source: "conversations",
      filter: conversationFilter,
    });

  const isLoading = ticketLoading || conversationLoading;
  const totalCount =
    (ticketCount?.count || 0) + (conversationCount?.count || 0);

  return (
    <TrendMetricCard
      key="total-quack-handled-summary"
      title="Total Quack Handled Items"
      tooltip={<TotalQuackHandledTooltip />}
    >
      <Flex
        className="h-full"
        direction="column"
        align="center"
        justify="center"
        gap="2"
      >
        {isLoading ? (
          <Text size="8" weight="bold" style={{ color: "var(--gray-9)" }}>
            Loading...
          </Text>
        ) : (
          <Text
            size="9"
            weight="bold"
            align="center"
            style={{
              fontSize: "3.5rem",
              lineHeight: "1",
            }}
          >
            {totalCount.toLocaleString()}
          </Text>
        )}

        <Flex gap="4" align="center">
          <Flex align="center" gap="2">
            <Ticket03Icon fill="blue" size={16} />
            <Text size="2" style={{ color: "var(--gray-11)" }}>
              {ticketCount?.count?.toLocaleString() || "0"} tickets
            </Text>
          </Flex>
          <Text size="2" style={{ color: "var(--gray-9)" }}>
            +
          </Text>
          <Flex align="center" gap="2">
            <Comment01Icon fill="orange" size={16} />
            <Text size="2" style={{ color: "var(--gray-11)" }}>
              {conversationCount?.count?.toLocaleString() || "0"} conversations
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </TrendMetricCard>
  );
}

function BillingPage() {
  const [selectedMonth, setSelectedMonth] = useState<number>(
    new Date().getMonth()
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );

  const dateRange = useMemo((): DateRange => {
    const startDate = new Date(selectedYear, selectedMonth, 1);
    const endDate = new Date(
      selectedYear,
      selectedMonth + 1,
      0,
      23,
      59,
      59,
      999
    );

    return {
      dateFrom: startDate.toISOString(),
      dateTo: endDate.toISOString(),
    };
  }, [selectedMonth, selectedYear]);

  const createFiltersForSource = useCallback(
    (source: ExploreInteractionType) => {
      const baseFilter =
        source === "conversations"
          ? {
              and: [{ subject: { isnotnull: "true" } }],
            }
          : undefined;

      const windowFilter = {
        and: [
          { createdAt: { gte: dateRange.dateFrom, lte: dateRange.dateTo } },
        ],
      };

      const filter = baseFilter
        ? mergeFilter(baseFilter, windowFilter)
        : windowFilter;

      const resolvedFilter = mergeFilter(filter, {
        and: [{ resolutionStatus: { in: ["RESOLVED"] } }],
      });

      const resolutionDistributionFilter = mergeFilter(filter, {
        and: [
          {
            resolutionStatus: {
              in: ["RESOLVED", "EXPECTED_ESCALATION", "UNEXPECTED_ESCALATION"],
            },
          },
        ],
      });

      const quackHandledFilter = mergeFilter(filter, {
        and: [
          {
            resolutionStatus: {
              in: ["RESOLVED", "EXPECTED_ESCALATION"],
            },
          },
        ],
      });

      return {
        baseFilter,
        filter,
        resolvedFilter,
        resolutionDistributionFilter,
        quackHandledFilter,
      };
    },
    [dateRange]
  );

  // Create filters for both tickets and conversations
  const ticketFilters = useMemo(
    () => createFiltersForSource("tickets"),
    [createFiltersForSource]
  );
  const conversationFilters = useMemo(
    () => createFiltersForSource("conversations"),
    [createFiltersForSource]
  );

  return (
    <PageLayoutV2>
      <Flex direction="column">
        <PageHeader
          title="Billing Dashboard"
          description="AI performance overview and analytics for billing"
          icon={<Home03Icon size={24} fill="gray" />}
          actions={
            <BillingHeaderActions
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              setSelectedMonth={setSelectedMonth}
              setSelectedYear={setSelectedYear}
            />
          }
        />

        <Section size="4" pt="0">
          <Flex p="6" direction="column" gap="6" width="100%">
            <TotalQuackHandledCard
              ticketFilter={ticketFilters.quackHandledFilter}
              conversationFilter={conversationFilters.quackHandledFilter}
            />
          </Flex>
        </Section>
      </Flex>
    </PageLayoutV2>
  );
}

export default BillingPage;
